// Object-pooled particle system using a single instanced quad draw call.
// Types: hit sparks, death explosions, pickup collect. Additive blending + bloom.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

export type ParticleType = 'hit' | 'death' | 'pickup';

interface Particle {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  color: THREE.Color;
  size: number;
  // >0 makes the quad expand as it fades (impact/death flashes).
  expand: number;
  // Per-particle velocity damping (default 0.92/update); shrapnel drags
  // harder so shards visibly stall after the initial kick.
  drag?: number;
  // Twinkle phase: shrapnel flickers in brightness as it dies.
  twinkle?: number;
  // Fade-out tint: the color lerps here as life runs down (white → family hue).
  fadeTo?: THREE.Color;
  // Stretch the quad along its velocity (shrapnel reads as streaks, not spray).
  streak?: boolean;
  // Optional homing target (e.g. soul mote flying to the player).
  target?: THREE.Vector3;
}

const PARTICLE_TYPE_COUNTS: Record<ParticleType, number> = {
  hit: 7,
  death: 18,
  pickup: 8
};

/** Kill staging, GW:RE2 idiom: a 2-tick white-hot point flash (instant-on,
 * never ramps), then a shrapnel cone. No filled discs — under sustained
 * kills their tails overlap into an opaque carpet that hides the player. */
const KILL_POP_SIZE = 0.42; // ≈24 px at desktop zoom
const KILL_POP_LIFE = 0.034; // ~2 frames at 60 fps
const KILL_GOLD = 0xffd36b; // destruction resolves to gold: reward, not threat
const SHARD_COUNT_MIN = 8; // 8–14 shards per kill
const SHARD_COUNT_SPREAD = 7;
const SHARD_SPEED = 11; // per-shard velocity variance: ×0.4–1.3
const SHARD_LIFE_MIN = 0.3; // twinkle-out window: 300–500 ms
const SHARD_LIFE_SPREAD = 0.2;

/** Big-soft-sprite budget (the mobile fill-rate bomb): at most ~20 concurrent
 * expanding flashes with a screen footprint at/above FLASH_BIG_FOOTPRINT.
 * Overflow degrades to a small non-expanding blink — never silence. Kill pops
 * (small, non-expanding) never count against the budget. */
const FLASH_BUDGET_MAX = 20;
const FLASH_BIG_FOOTPRINT = 0.7;

/** Pool tail reserved for kill reactions so deaths never go silent at mass
 * kill rates; ambient effects stop short of it. */
const PARTICLE_RESERVE = 128;

const PARTICLE_TYPE_COLORS: Record<ParticleType, readonly number[]> = {
  hit: [0xffc84d, 0xfff6dc, 0x35e0ff],
  death: [0x35e0ff, 0x7febff, 0xffffff],
  pickup: [0x7dff4f, 0xd6ffb0, 0xffffff]
};

/** Soft radial dot texture: bare quads read as crisp "popcorn" once bloom and
 * CRT rounding get to them; a gradient falloff keeps particles as energy. */
let softDotTexture: THREE.CanvasTexture | null = null;
function makeSoftDotTexture(): THREE.CanvasTexture | null {
  if (softDotTexture) return softDotTexture;
  // Headless (vitest/node): no canvas — the material renders untextured.
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(16, 16, 1, 16, 16, 16);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.7)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  softDotTexture = new THREE.CanvasTexture(canvas);
  return softDotTexture;
}

export class ParticleSystem {
  readonly mesh: THREE.InstancedMesh;
  private readonly particles: Particle[] = [];
  private readonly dummy = new THREE.Object3D();
  private readonly tmpColor = new THREE.Color();
  private readonly capacity: number;
  private reducedParticles = false;
  /** Death bursts spawned (debug overlay diagnostics). */
  dbgDeathBursts = 0;

  /** Live particle count (debug overlay diagnostics). */
  get aliveCount(): number {
    return this.particles.length;
  }

  constructor(capacity: number) {
    this.capacity = capacity;
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: makeSoftDotTexture(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
  }

  setReducedParticles(enabled: boolean): void {
    this.reducedParticles = enabled;
  }

  spawnHit(pos: Vec2, normal?: Vec2): void {
    this.spawnBurst('hit', pos, normal);
    // Short impact flash so projectile hits read inside dense swarms.
    this.spawnFlash(pos, 0xfff3cf, 0.6, 0.09, 0.9);
  }

  /**
   * Standard kill staging — POP → SHRAPNEL:
   * 1. a 2-tick white-hot point flash (instant-on, no ramp-up);
   * 2. 8–14 white→`tint` shards with per-shard velocity/size/drag variance,
   *    heavy drag so they stall, twinkling out over 300–500 ms.
   * `tint` sets the shard fade-out color: the kill path passes the victim's
   * family hue (red/pink already collapsed to gold in micro-events), so each
   * kill cone cools toward ONE hue instead of generic confetti.
   */
  spawnDeath(pos: Vec2, tint = KILL_GOLD): void {
    this.dbgDeathBursts++;
    this.spawnKillPop(pos, KILL_POP_SIZE);
    this.spawnShards(pos, tint, SHARD_COUNT_MIN, SHARD_COUNT_SPREAD, 1);
  }

  /** Elite/boss kill: the standard staging scaled up, plus a fast thin ring.
   * Shards take the family tint like any kill; the ring stays GOLD on every
   * kill — the reward read that marks a heavy kill. */
  spawnDeathElite(pos: Vec2, tint = KILL_GOLD): void {
    this.dbgDeathBursts++;
    this.spawnKillPop(pos, KILL_POP_SIZE * 2.2);
    this.spawnShards(pos, tint, 14, 7, 1.2);
    this.spawnRing(pos, KILL_GOLD, 12, 14, 0.3);
  }

  /** Violent shard spray in the caller's hue (boss entrance flare, player
   * death): the same shrapnel idiom, more and faster shards. */
  spawnDeathShards(pos: Vec2, color?: number): void {
    this.dbgDeathBursts++;
    this.spawnKillPop(pos, KILL_POP_SIZE * 1.6);
    this.spawnShards(pos, color ?? KILL_GOLD, 12, 7, 1.35);
  }

  /** Soft variant: a dim pop plus slow embers drifting off the wreck, so the
   * enemy never simply vanishes. Kept for the debug tooling; the combat kill
   * path is uniform POP → SHRAPNEL. */
  spawnDeathDissolve(pos: Vec2, color?: number): void {
    this.dbgDeathBursts++;
    const tint = color ?? KILL_GOLD;
    this.spawnKillPop(pos, KILL_POP_SIZE * 0.8);
    for (let i = 0; i < 10; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dir = new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
      const speed = 2.5 + Math.random() * 2.5;
      const life = 0.4 + Math.random() * 0.3;
      const p: Particle = {
        pos: new THREE.Vector3(pos.x, pos.y, 0.1),
        vel: dir.multiplyScalar(speed),
        life,
        maxLife: life,
        color: new THREE.Color(0xfff6dc).multiplyScalar(0.9),
        size: 0.2 + Math.random() * 0.15,
        expand: 0,
        drag: 0.94,
        twinkle: Math.random() * Math.PI * 2,
        fadeTo: new THREE.Color(tint).multiplyScalar(0.6),
      };
      if (this.particles.length >= this.capacity) this.evictOne();
      this.particles.push(p);
    }
  }

  /**
   * Player-death blaze (canvasui.dev blaze component, adapted): a violent
   * eruption of fire sparks in the component's palette — spark orange
   * [1,0.4,0.05] → 0xff660d, smoke amber 0xff6e1a, hot gold, white core.
   * Death-adjacent, so it may draw on the kill reserve tail.
   */
  spawnBlaze(pos: Vec2): void {
    const colors = [0xff660d, 0xff9540, 0xffd36b, 0xfff6dc, 0xd9381e];
    for (let i = 0; i < 30; i++) {
      if (this.particles.length >= this.capacity) break;
      const angle = Math.random() * Math.PI * 2;
      const dir = new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
      const speed = 5 + Math.random() * 9;
      const life = 0.35 + Math.random() * 0.4;
      const p: Particle = {
        pos: new THREE.Vector3(pos.x + dir.x * 0.15, pos.y + dir.y * 0.15, 0.12),
        vel: dir.multiplyScalar(speed),
        life,
        maxLife: life,
        color: new THREE.Color(colors[i % colors.length]),
        size: 0.5 + Math.random() * 0.4,
        expand: 0,
      };
      this.particles.push(p);
    }
    // A dozen embers already rising off the blast.
    for (let i = 0; i < 12; i++) this.spawnEmber(pos, true);
    // White-hot core flash.
    this.spawnFlash(pos, 0xfff3cf, 2.4, 0.16, 1.6);
  }

  /**
   * One rising ember (or a dim smoke wisp) for the death blaze's slow phase:
   * drifts up off the wreck, decelerating, cooling from spark orange to
   * ember red as it fades. Ambient — never touches the kill reserve.
   */
  spawnEmber(pos: Vec2, hot = false): void {
    if (this.particles.length >= this.capacity - PARTICLE_RESERVE) return;
    const smoke = !hot && Math.random() < 0.35;
    const life = smoke ? 1.1 + Math.random() * 0.6 : 0.7 + Math.random() * 0.6;
    const p: Particle = {
      pos: new THREE.Vector3(
        pos.x + (Math.random() - 0.5) * 0.8,
        pos.y + (Math.random() - 0.5) * 0.8,
        0.11
      ),
      vel: new THREE.Vector3(
        (Math.random() - 0.5) * 1.6,
        1.8 + Math.random() * 2.4,
        0
      ),
      life,
      maxLife: life,
      color: new THREE.Color(
        smoke
          ? 0x5a1408
          : [0xff660d, 0xff9540, 0xffd36b, 0xd9381e][Math.floor(Math.random() * 4)]
      ),
      size: smoke ? 0.6 + Math.random() * 0.3 : 0.34 + Math.random() * 0.22,
      expand: smoke ? 1.4 : 0,
    };
    this.particles.push(p);
  }

  spawnPickup(pos: Vec2): void {
    this.spawnBurst('pickup', pos);
  }

  // Small gold muzzle flash where a projectile leaves the player.
  spawnMuzzle(pos: Vec2): void {
    this.spawnFlash(pos, 0xffe1a0, 0.8, 0.09, 1.5);
  }

  /** A single bright soul mote that flies from a death location to the player. */
  spawnSoul(pos: Vec2, color: number, target: Vec2): void {
    if (this.particles.length >= this.capacity) this.evictOne();
    const p: Particle = {
      pos: new THREE.Vector3(pos.x, pos.y, 0.12),
      vel: new THREE.Vector3(0, 0, 0),
      life: 0.6,
      maxLife: 0.6,
      color: new THREE.Color(color),
      size: 0.5,
      expand: 0,
      target: new THREE.Vector3(target.x, target.y, 0.12),
    };
    this.particles.push(p);
  }

  /** A small dust puff for movement start/stop. */
  spawnDust(pos: Vec2, dir: Vec2): void {
    if (this.particles.length >= this.capacity) this.evictOne();
    const angle = Math.atan2(dir.y, dir.x) + Math.PI + (Math.random() - 0.5) * 0.8;
    const speed = 1.5 + Math.random() * 1.5;
    const life = 0.25 + Math.random() * 0.15;
    const p: Particle = {
      pos: new THREE.Vector3(pos.x, pos.y, 0.08),
      vel: new THREE.Vector3(Math.cos(angle) * speed, Math.sin(angle) * speed, 0),
      life,
      maxLife: life,
      color: new THREE.Color(0x8fb3cc),
      size: 0.25,
      expand: 0,
    };
    this.particles.push(p);
  }

  spawnFlash(pos: Vec2, color: number, size: number, life: number, expand = 0): void {
    // Death-adjacent effects may use the reserve; everything else stops early.
    if (this.particles.length >= this.capacity - PARTICLE_RESERVE) return;
    // Size-aware budget on big soft expanding sprites (fill-rate + additive
    // clip governor): once ~20 are live, new ones degrade to a small
    // non-expanding blink — feedback never goes fully silent.
    if (expand > 0 && size * (1 + expand) >= FLASH_BIG_FOOTPRINT) {
      let activeBig = 0;
      for (const p of this.particles) {
        if (p.expand > 0 && p.size * (1 + p.expand) >= FLASH_BIG_FOOTPRINT) activeBig++;
      }
      if (activeBig >= FLASH_BUDGET_MAX) {
        expand = 0;
        size = Math.min(size, 0.35);
      }
    }
    if (this.reducedParticles) {
      size *= 0.6;
    }
    const p: Particle = {
      pos: new THREE.Vector3(pos.x, pos.y, 0.12),
      vel: new THREE.Vector3(0, 0, 0),
      life,
      maxLife: life,
      color: new THREE.Color(color),
      size,
      expand,
    };
    this.particles.push(p);
  }

  /** The kill POP: a 2-tick white-hot point flash. Instant-on (expand 0 —
   * the old flashes ramped UP over frames and read as fog), HDR-pushed so
   * bloom catches it for exactly those two frames. */
  private spawnKillPop(pos: Vec2, size: number): void {
    if (this.particles.length >= this.capacity) this.evictOne();
    if (this.reducedParticles) size *= 0.6;
    const p: Particle = {
      pos: new THREE.Vector3(pos.x, pos.y, 0.12),
      vel: new THREE.Vector3(0, 0, 0),
      life: KILL_POP_LIFE,
      maxLife: KILL_POP_LIFE,
      color: new THREE.Color(0xfff6dc).multiplyScalar(2.2),
      size,
      expand: 0,
    };
    this.particles.push(p);
  }

  /**
   * Kill SHRAPNEL: `min .. min+spread-1` crisp streaks kicked out at 0.4–1.3×
   * the base speed, each with its own size, drag and twinkle phase — a spray
   * of identical sparks reads as fog, not shrapnel. Heavy drag stalls them
   * after the kick; they cool white → `tint` and twinkle out over 300–500 ms.
   */
  private spawnShards(pos: Vec2, tint: number, min: number, spread: number, speedMul: number): void {
    let count = min + Math.floor(Math.random() * spread);
    if (this.reducedParticles) count = Math.max(4, Math.floor(count * 0.5));
    const end = new THREE.Color(tint);
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= this.capacity) this.evictOne();
      const angle = Math.random() * Math.PI * 2;
      const speed = SHARD_SPEED * speedMul * (0.4 + Math.random() * 0.9);
      const life = SHARD_LIFE_MIN + Math.random() * SHARD_LIFE_SPREAD;
      const p: Particle = {
        pos: new THREE.Vector3(pos.x, pos.y, 0.11),
        vel: new THREE.Vector3(Math.cos(angle) * speed, Math.sin(angle) * speed, 0),
        life,
        maxLife: life,
        color: new THREE.Color(0xfff6dc).multiplyScalar(1.6),
        size: 0.14 + Math.random() * 0.16,
        expand: 0,
        drag: 0.86 + Math.random() * 0.05,
        twinkle: Math.random() * Math.PI * 2,
        fadeTo: end.clone().multiplyScalar(0.5 + Math.random() * 0.7),
        streak: true,
      };
      this.particles.push(p);
    }
  }

  /** Spawn a single particle at a position with velocity. */
  spawnParticleAt(pos: Vec2, vel: THREE.Vector3, life: number, color: number, size: number, expand: number): void {
    if (this.particles.length >= this.capacity) this.evictOne();
    // Non-finite guard: one NaN matrix poisons the whole instanced draw.
    if (!Number.isFinite(pos.x + pos.y + vel.x + vel.y + life + size)) return;
    const p: Particle = {
      pos: new THREE.Vector3(pos.x, pos.y, 0.1),
      vel,
      life,
      maxLife: life,
      color: new THREE.Color(color),
      size,
      expand,
    };
    this.particles.push(p);
  }

  /**
   * Pool-full eviction: drop the lowest-life particle (the one closest to
   * expiring anyway). A saturated pool must never go silent — kill feedback
   * (deaths, pickups) always gets room, whatever the fight looks like.
   */
  private evictOne(): void {
    let minIdx = 0;
    let minLife = Infinity;
    for (let i = 0; i < this.particles.length; i++) {
      const l = this.particles[i].life;
      if (l < minLife) {
        minLife = l;
        minIdx = i;
      }
    }
    const last = this.particles.pop();
    if (last && minIdx < this.particles.length) {
      this.particles[minIdx] = last;
    }
  }

  private spawnRing(pos: Vec2, colorHex: number, count: number, speed: number, life: number): void {
    if (this.reducedParticles) {
      count = Math.max(4, Math.floor(count * 0.5));
    }
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= this.capacity) this.evictOne();
      const angle = (i / count) * Math.PI * 2;
      const dir = new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
      const p: Particle = {
        pos: new THREE.Vector3(pos.x + dir.x * 0.15, pos.y + dir.y * 0.15, 0.1),
        vel: dir.multiplyScalar(speed * (0.85 + Math.random() * 0.3)),
        life,
        maxLife: life,
        color: new THREE.Color(colorHex),
        size: 0.4,
        expand: 0,
      };
      this.particles.push(p);
    }
  }

  private spawnBurst(type: ParticleType, pos: Vec2, normal?: Vec2, baseColor?: number): void {
    if (type === 'death') this.dbgDeathBursts++;
    let count = PARTICLE_TYPE_COUNTS[type];
    if (this.reducedParticles) {
      count = Math.max(1, Math.floor(count * 0.5));
    }
    const colors = PARTICLE_TYPE_COLORS[type];
    const speedBase = type === 'death' ? 9 : type === 'hit' ? 6 : 3;
    // Kill reactions may consume the reserve tail of the pool; ambient
    // effects stop short of it so a mass kill never goes silent. At the limit
    // the lowest-life particles are EVICTED — feedback never mutes itself.
    const limit = type === 'death' ? this.capacity : this.capacity - PARTICLE_RESERVE;
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= limit) this.evictOne();
      const angle = Math.random() * Math.PI * 2;
      const dir = normal && (i & 1)
        ? new THREE.Vector3(normal.x, normal.y, 0).normalize()
        : new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
      const speed = speedBase * (0.4 + Math.random() * 0.8);
      // Death sparks capped under 0.5 s so kills resolve crisply.
      const life = (type === 'death' ? 0.4 : 0.2) + Math.random() * (type === 'death' ? 0.3 : 0.25);
      const colorHex = baseColor !== undefined ? this.tintColor(baseColor, i) : colors[i % colors.length];
      const p: Particle = {
        pos: new THREE.Vector3(pos.x + dir.x * 0.1, pos.y + dir.y * 0.1, 0.1),
        vel: dir.multiplyScalar(speed),
        life,
        maxLife: life,
        color: new THREE.Color(colorHex),
        size: type === 'death' ? 0.6 + Math.random() * 0.4 : 0.25 + Math.random() * 0.2,
        expand: 0
      };
      this.particles.push(p);
    }
  }

  // Derive three shades from a base color for tinted death bursts.
  private tintColor(base: number, i: number): number {
    const c = new THREE.Color(base);
    if (i % 3 === 1) c.offsetHSL(0, 0.1, 0.15);
    if (i % 3 === 2) c.offsetHSL(0, -0.05, -0.15);
    return c.getHex();
  }

  update(dt: number): void {
    let i = 0;
    while (i < this.particles.length) {
      const p = this.particles[i];
      p.life -= dt;
      // Cull dead AND non-finite entries: a NaN position must never reach
      // the instanced mesh (one poisoned matrix blanks every particle).
      if (p.life <= 0 || !Number.isFinite(p.pos.x + p.pos.y + p.vel.x + p.vel.y + p.life)) {
        const last = this.particles.pop();
        if (last && i < this.particles.length) {
          this.particles[i] = last;
        }
        continue;
      }
      if (p.target) {
        const dx = p.target.x - p.pos.x;
        const dy = p.target.y - p.pos.y;
        const dist = Math.hypot(dx, dy);
        if (dist > 0.05) {
          const speed = 6;
          p.vel.x = (dx / dist) * speed;
          p.vel.y = (dy / dist) * speed;
        }
      }
      p.pos.addScaledVector(p.vel, dt);
      if (!p.target) {
        p.vel.multiplyScalar(p.drag ?? 0.92);
      }
      i++;
    }
  }

  finalize(): void {
    const alive = this.particles.length;
    for (let i = 0; i < alive; i++) {
      const p = this.particles[i];
      const t = p.life / p.maxLife;
      this.dummy.position.copy(p.pos);
      // Expanding flashes grow as they fade; sparks shrink as they fade.
      const scale = p.expand > 0
        ? p.size * (0.35 + (1 - t) * p.expand) * (0.4 + 0.6 * t)
        : p.size * Math.sqrt(t);
      if (p.streak) {
        // Shrapnel streak: orient along the velocity and stretch with speed;
        // stalled shards relax back to twinkling dots as drag eats the kick.
        this.dummy.rotation.z = Math.atan2(p.vel.y, p.vel.x);
        const stretch = 1 + Math.min(2, p.vel.length() * 0.12);
        this.dummy.scale.set(scale * stretch, scale * 0.55, 1);
      } else {
        this.dummy.rotation.z = 0;
        this.dummy.scale.setScalar(scale);
      }
      this.dummy.updateMatrix();
      this.tmpColor.copy(p.color);
      // Cool from the birth color toward the fade-out tint as life runs down.
      if (p.fadeTo) this.tmpColor.lerp(p.fadeTo, 1 - t);
      this.tmpColor.multiplyScalar(t);
      // Shrapnel twinkle: brightness flicker keyed off remaining life.
      if (p.twinkle !== undefined) {
        this.tmpColor.multiplyScalar(0.65 + 0.35 * Math.sin(p.life * 46 + p.twinkle));
      }
      this.mesh.setMatrixAt(i, this.dummy.matrix);
      this.mesh.setColorAt(i, this.tmpColor);
    }
    this.mesh.count = alive;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) {
      this.mesh.instanceColor.needsUpdate = true;
    }
  }
}
